import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { walletApi } from '@/services/api'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'

interface CreateWalletModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export function CreateWalletModal({ isOpen, onClose, onSuccess }: CreateWalletModalProps) {
  const { t } = useTranslation()
  const [currency, setCurrency] = useState<string>('STN')
  const [dailyLimit, setDailyLimit] = useState<string>('1000')
  const [monthlyLimit, setMonthlyLimit] = useState<string>('10000')
  const [isLoading, setIsLoading] = useState(false)

  const currencies = [
    { value: 'STN', label: 'Dobra São Tomé (STN)' },
    { value: 'EUR', label: 'Euro (EUR)' },
    { value: 'USD', label: 'Dólar Americano (USD)' },
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!currency) {
      toast.error(t('wallet.selectCurrency') || 'Por favor, selecione uma moeda')
      return
    }

    const dailyLimitValue = parseFloat(dailyLimit)
    const monthlyLimitValue = parseFloat(monthlyLimit)

    if (isNaN(dailyLimitValue) || dailyLimitValue <= 0) {
      toast.error(t('wallet.invalidDailyLimit') || 'Limite diário inválido')
      return
    }

    if (isNaN(monthlyLimitValue) || monthlyLimitValue <= 0) {
      toast.error(t('wallet.invalidMonthlyLimit') || 'Limite mensal inválido')
      return
    }

    if (monthlyLimitValue < dailyLimitValue) {
      toast.error(t('wallet.monthlyLimitMustBeGreater') || 'O limite mensal deve ser maior que o limite diário')
      return
    }

    setIsLoading(true)

    try {
      await walletApi.createWallet({
        currency,
        daily_limit: dailyLimitValue,
        monthly_limit: monthlyLimitValue,
      })

      toast.success(t('wallet.walletCreated') || 'Carteira criada com sucesso')
      onSuccess()
      onClose()
      // Reset form
      setCurrency('STN')
      setDailyLimit('1000')
      setMonthlyLimit('10000')
    } catch (error: any) {
      console.error('Error creating wallet:', error)
      
      // Handle different error formats
      let errorMessage = t('wallet.createWalletError') || 'Erro ao criar carteira'
      
      if (error.response?.data) {
        const errorData = error.response.data
        
        // Check for currency error (already exists)
        if (errorData.currency) {
          if (Array.isArray(errorData.currency)) {
            errorMessage = errorData.currency[0]
          } else if (typeof errorData.currency === 'string') {
            errorMessage = errorData.currency
          } else {
            errorMessage = t('wallet.walletAlreadyExists') || 'Você já possui uma carteira com esta moeda'
          }
        } 
        // Check for general error field
        else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : errorData.error[0]
        }
        // Check for detail field
        else if (errorData.detail) {
          errorMessage = errorData.detail
        }
        // Check for non_field_errors
        else if (errorData.non_field_errors) {
          errorMessage = Array.isArray(errorData.non_field_errors) 
            ? errorData.non_field_errors[0] 
            : errorData.non_field_errors
        }
      }
      
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[100]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  {t('wallet.createWallet') || 'Criar Nova Carteira'}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  disabled={isLoading}
                >
                  <X className="h-5 w-5 text-text-secondary" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Currency Selection */}
                <div>
                  <label htmlFor="currency" className="block text-sm font-medium text-text-secondary mb-1">
                    {t('wallet.currency') || 'Moeda'}
                  </label>
                  <select
                    id="currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none transition-colors"
                    required
                    disabled={isLoading}
                  >
                    {currencies.map((curr) => (
                      <option key={curr.value} value={curr.value}>
                        {curr.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Daily Limit */}
                <div>
                  <label htmlFor="dailyLimit" className="block text-sm font-medium text-text-secondary mb-1">
                    {t('wallet.dailyLimit') || 'Limite Diário'}
                  </label>
                  <Input
                    type="number"
                    placeholder="1000"
                    value={dailyLimit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDailyLimit(e.target.value)}
                    min="0.01"
                    step="0.01"
                    required
                    disabled={isLoading}
                  />
                  <p className="text-xs text-text-tertiary mt-1">
                    {t('wallet.dailyLimitDescription') || 'Limite máximo de transações por dia'}
                  </p>
                </div>

                {/* Monthly Limit */}
                <div>
                  <label htmlFor="monthlyLimit" className="block text-sm font-medium text-text-secondary mb-1">
                    {t('wallet.monthlyLimit') || 'Limite Mensal'}
                  </label>
                  <Input
                    type="number"
                    placeholder="10000"
                    value={monthlyLimit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMonthlyLimit(e.target.value)}
                    min="0.01"
                    step="0.01"
                    required
                    disabled={isLoading}
                  />
                  <p className="text-xs text-text-tertiary mt-1">
                    {t('wallet.monthlyLimitDescription') || 'Limite máximo de transações por mês'}
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                    disabled={isLoading}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('wallet.creating') || 'Criando...'}
                      </>
                    ) : (
                      t('wallet.create') || 'Criar'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

